import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, User } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { publicApi, mediaUrl } from "../lib/api";
import Seo, { breadcrumbSchema } from "../components/Seo";

const StaffCard = ({ member, index }) => {
  const p1 = mediaUrl(member.photo1, 600);
  const p2 = mediaUrl(member.photo2, 600);
  const hasPhoto = !!p1;
  const paragraphs = (member.bio || "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <Reveal delay={(index % 3) * 80}>
      <div className="group h-full bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300">
        {/* Photo with hover rotate between two images */}
        <div className="relative aspect-[4/5] overflow-hidden bg-navy/5">
          {hasPhoto ? (
            <>
              <img loading="lazy" decoding="async" src={p1} alt={member.name}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${p2 ? "group-hover:opacity-0" : ""}`} />
              {p2 && (
                <img loading="lazy" decoding="async" src={p2} alt={`${member.name} alternate`}
                  className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:scale-105" />
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-navy to-navy-light">
              <span className="font-display font-extrabold text-6xl text-white/90">{member.name.charAt(0)}</span>
            </div>
          )}
          {/* name band */}
          <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-navy-deep/90 via-navy-deep/40 to-transparent">
            <h3 className="font-display font-extrabold text-xl text-white leading-tight">{member.name}</h3>
            <p className="text-orange text-sm font-semibold">{member.role}</p>
          </div>
        </div>
        {/* bio */}
        <div className="p-6">
          {paragraphs.map((para, i) => (
            <p key={i} className="text-sm text-slate-500 leading-relaxed mb-3 last:mb-0">{para}</p>
          ))}
        </div>
      </div>
    </Reveal>
  );
};

export default function Team() {
  const [staff, setStaff] = useState([]);
  useEffect(() => { publicApi.staff().then(setStaff).catch(() => {}); }, []);

  return (
    <div>
      <Seo title="Meet the Team — Your Dedicated Client Managers" description="Meet Team CASS — friendly, qualified accountants and dedicated client managers who act as your outsourced finance team and genuinely care about helping your business grow." jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Meet the Team", path: "/team" }])} />
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">Meet the people</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">The Team</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
            We're more than numbers — we're a friendly, qualified team who genuinely care about your business. Say hello to Team CASS.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionTitle eyebrow="Team CASS" title="The friendly faces behind your finances" subtitle="Hover over each photo to say hello!" center /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {staff.map((m, i) => <StaffCard key={m.id} member={m} index={i} />)}
          </div>
        </div>
      </section>

      <section className="py-16 bg-navy text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Want to join Team CASS?</h2>
          <p className="text-slate-300 mb-8">We're always keen to hear from talented, friendly people. Get in touch.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Let's Talk <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
